import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './FreelancerEarnings.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function FreelancerEarnings() {
  const { currentUser } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState({
    total_earnings: 0,
    earnings_by_month: {},
    earnings_by_business: {},
    assignments: []
  });

  const loadEarnings = async (fromDate, toDate) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      const res = await fetch(
        `${API_BASE_URL}/api/freelancer/earnings?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (!res.ok) {
        throw new Error('Failed to load earnings');
      }
      const json = await res.json();
      setData(json || {});
    } catch (e) {
      console.error('Failed to load freelancer earnings', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    loadEarnings(from, to);
  }, [currentUser]);

  if (!currentUser) {
    return null;
  }

  const monthEntries = Object.entries(data.earnings_by_month || {}).sort(
    ([a], [b]) => (a > b ? 1 : -1)
  );


  return (
    <div
      className={`freelancer-earnings-container ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="freelancer-earnings-header">
        <div className="freelancer-earnings-header-content">
          <div className="freelancer-earnings-header-left">
            <button
              className="freelancer-earnings-back-button"
              onClick={() => navigate('/freelancer/dashboard')}
              aria-label={t('common.back') || 'Back'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>{t('common.back') || 'Back'}</span>
            </button>
          </div>
          <div className="freelancer-earnings-title">
            <h1>{t('freelancerEarnings.title') || 'My Earnings'}</h1>
            <p>
              {t('freelancerEarnings.subtitle') ||
                'Track how much you earned over time.'}
            </p>
          </div>
        </div>
      </header>

      <main className="freelancer-earnings-main">
        <section className="freelancer-earnings-filters">
          <div className="earnings-filter-field">
            <label>{t('freelancerEarnings.from') || 'From'}</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="earnings-filter-field">
            <label>{t('freelancerEarnings.to') || 'To'}</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button
            className="earnings-apply-button"
            onClick={() => loadEarnings(from, to)}
          >
            {t('common.apply') || 'Apply'}
          </button>
        </section>

        <section className="freelancer-earnings-summary">
          <div className="earnings-summary-card">
            <div className="earnings-summary-label">
              {t('freelancerEarnings.total') || 'Total earnings'}
            </div>
            <div className="earnings-summary-value">
              ₪{Number(data.total_earnings || 0).toLocaleString()}
            </div>
          </div>
        </section>

        <section className="freelancer-earnings-section">
          <h3>{t('freelancerEarnings.byMonth') || 'By month'}</h3>
          {loading ? (
            <div className="freelancer-earnings-loading">
              {t('common.loading')}
            </div>
          ) : monthEntries.length === 0 ? (
            <p className="freelancer-earnings-empty">
              {t('freelancerEarnings.empty') ||
                'No earnings found for the selected period.'}
            </p>
          ) : (
            <ul className="freelancer-earnings-list">
              {monthEntries.map(([month, value]) => (
                <li key={month} className="freelancer-earnings-item">
                  <span>{month}</span>
                  <span>₪{Number(value || 0).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="freelancer-earnings-section">
          <h3>{t('freelancerEarnings.assignments') || 'Completed Assignments'}</h3>
          {loading ? (
            <div className="freelancer-earnings-loading">
              {t('common.loading')}
            </div>
          ) : !data.assignments || data.assignments.length === 0 ? (
            <p className="freelancer-earnings-empty">
              {t('freelancerEarnings.noAssignments') ||
                'No completed assignments found for the selected period.'}
            </p>
          ) : (
            <ul className="freelancer-earnings-list">
              {data.assignments.map((assignment) => {
                const date = new Date(assignment.date);
                const dateStr = date.toLocaleDateString();
                return (
                  <li
                    key={assignment.id}
                    className="freelancer-earnings-item freelancer-earnings-clickable"
                    onClick={() => navigate(`/job/${assignment.id}`)}
                  >
                    <div className="freelancer-earnings-assignment-info">
                      <span className="freelancer-earnings-assignment-title">
                        {assignment.job_title || t('freelancerEarnings.assignment') || 'Assignment'}
                      </span>
                      <span className="freelancer-earnings-assignment-date">
                        {dateStr}
                      </span>
                    </div>
                    <span className="freelancer-earnings-assignment-amount">
                      ₪{Number(assignment.pay_amount || 0).toLocaleString()}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}


